/**
 * Fetch Service
 * 
 * Responsible for HTTP operations and simple data fetching.
 * Does NOT contain processing logic - use processors for that.
 */

import axios from 'axios';
import { Geeklist } from '../objects';

/**
 * Fetch raw response from a URL
 * 
 * @param {string} url - URL to fetch
 * @returns {Promise<Object|null>} Axios response or null on error
 */
export const fetchResponse = async (url) => {
    try {
        const response = await axios.get(url);
        return response;
    } catch (error) {
        console.error("Error fetching data from", url);
        return null;
    }
};

/**
 * Fetch data from a URL and return the response body
 * 
 * @param {string} url - URL to fetch
 * @returns {Promise<Object|null>} Response data or null on error
 */
export const fetchData = async (url) => {
    try {
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        console.error("Error fetching data from", url);
        return null;
    }
};

/**
 * Fetch geeklists from BGG API
 * Fetches 10 pages of recent geeklists
 * 
 * @param {string} numPages - Number of pages to fetch
 * @returns {Promise<{geeklists: Array}>} Object containing array of geeklist metadata
 */
export const fetchGeeklists = async (numPages) => {
    try {
        // Fetch numPages of pages of geeklists, fallback to 10
        const pagesToFetch = numPages || 10;
        const fetchPromises = [];
        for (let page = 1; page <= pagesToFetch; page++) {
            const url = `/api/geeklists?partial=listing&sort=recent&category=trade&pageid=${page}&domain=boardgame`;
            fetchPromises.push(axios.get(url));
        }
        
        // Wait for all requests to complete
        const responses = await Promise.all(fetchPromises);
        
        // Process and combine the responses
        let allGeeklists = [];
        
        responses.forEach(response => {
            if (response.data && response.data.data) {
                const pageGeeklists = response.data.data.map(geeklist => 
                    Geeklist.fromApiResponse(geeklist)
                );
                
                allGeeklists = [...allGeeklists, ...pageGeeklists];
            }
        });
        
        return { geeklists: allGeeklists };
    } catch (error) {
        console.error("Error fetching geeklist data:", error);
        return { geeklists: [] };
    }
};

const fetchService = {
    fetchResponse,
    fetchData,
    fetchGeeklists
};

export default fetchService;
